// src/services/sheetPatientsService.js
//
// Reads the legacy patient Google Sheet (the lin-dubois1-only store) through the
// getPatientsFromSheet Cloud Function.
//
// WHY THIS EXISTS
// ---------------
// Callers used to do `const res = await fetch(url); const data = await res.json()`
// with no res.ok check. When the upstream Google Sheets API has a wobble the
// function returns 500 with the plain-text body "Error fetching patient data.",
// so res.json() throws a JSON parse error, the caller's catch swallows it, and
// the page renders as though the patient list were legitimately EMPTY.
//
// That failure mode is the dangerous one here: "the sheet is down" and "this
// patient is not in the system" look identical to a coordinator. This module
// makes the difference explicit by throwing a typed error, so the UI can say
// "temporarily unavailable" instead of quietly showing nothing.
//
// Observed 2026-08-13: the Sheets API returned HTTP 503 for several minutes and
// the Coordinator page showed an empty list with only a misleading CORS error in
// the console.

import { FUNCTIONS_BASE } from "../config/functionsBase";

/** Thrown when there is no signed-in user to authenticate the call with. */
export class NotSignedInError extends Error {
  constructor(message = "You must be signed in to read patient data.") {
    super(message);
    this.name = "NotSignedInError";
  }
}

// D-1 stage 2 — every call to the sheet endpoints now proves who is making it.
//
// getPatientsFromSheet and updatePatientInSheet are public HTTPS addresses. Until
// this, they answered anyone: the first returns the entire patient list (names,
// dates of birth, MRNs, clinical notes), the second edits a record. Neither
// checked the caller, and the addresses are compiled into the JavaScript every
// visitor downloads, so knowing the URL was enough.
//
// The pages already know who their user is — they signed in and passed 2FA. They
// simply never mentioned it when calling out. This attaches that proof.
//
// firebase is imported LAZILY, inside the function, on purpose: a static import
// would initialise the Firebase app whenever this module is imported, including
// from its unit test. Keeping the module Firebase-free at import time is the same
// split calendarConfigSchema.js uses. Tests inject `authHeaders` and never reach
// this path.
async function defaultAuthHeaders() {
  const { auth } = await import("../firebase");
  const user = auth.currentUser;
  if (!user) throw new NotSignedInError();
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}

/** Thrown when the sheet could not be read after all retries. */
export class SheetUnavailableError extends Error {
  constructor(message, { status = null, attempts = 0, cause = null } = {}) {
    super(message);
    this.name = "SheetUnavailableError";
    this.status = status; // HTTP status of the last attempt, if any
    this.attempts = attempts;
    this.cause = cause;
  }
}

// Retry only what might succeed on a second look: network failures (status null)
// plus 429 and 5xx. A 400/403/404 is a real answer — retrying just delays the
// error and hammers a function that is telling us something definite.
const isTransient = (status) => status === null || status === 429 || status >= 500;

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch patients from the legacy sheet, retrying transient failures.
 *
 * @param {object}   [opts]
 * @param {number}   [opts.retries=2]       retries AFTER the first attempt
 * @param {number}   [opts.baseDelayMs=600] first backoff; doubles each retry
 * @param {function} [opts.fetchImpl]       injected for tests
 * @param {function} [opts.sleep]           injected so tests do not actually wait
 * @param {function} [opts.authHeaders]     injected for tests; returns request headers
 * @returns {Promise<Array>} the patient rows
 * @throws  {SheetUnavailableError} the sheet could not be read
 * @throws  {NotSignedInError}      nobody is signed in to authenticate the call
 */
export async function fetchSheetPatients({
  retries = 2,
  baseDelayMs = 600,
  fetchImpl,
  sleep = defaultSleep,
  authHeaders = defaultAuthHeaders,
} = {}) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const url = `${FUNCTIONS_BASE}/getPatientsFromSheet`;

  // Resolved ONCE, outside the retry loop. An ID token is good for an hour, so
  // re-minting it per attempt would buy nothing and slow the backoff down. This
  // throws before any request is made if nobody is signed in — the caller finds
  // out immediately rather than after three attempts and two backoffs.
  const headers = await authHeaders();

  let lastStatus = null;
  let lastCause = null;

  for (let attempt = 1; attempt <= retries + 1; attempt += 1) {
    let status = null;
    try {
      const res = await doFetch(url, { headers });
      status = typeof res.status === "number" ? res.status : null;

      if (res.ok) {
        const data = await res.json();
        // A non-array body means the function answered with something we cannot
        // use (an error string, an HTML error page). Treat it as a failure
        // rather than handing callers something their .map() will choke on.
        if (!Array.isArray(data)) {
          throw new Error("Sheet response was not an array");
        }
        return data;
      }
      lastStatus = status;
      lastCause = new Error(`HTTP ${status}`);
    } catch (err) {
      // Network failure, JSON parse failure, or the array guard above.
      lastStatus = status;
      lastCause = err;
    }

    const canRetry = attempt <= retries && isTransient(lastStatus);
    if (!canRetry) break;

    // Exponential backoff with jitter, so several tabs retrying do not line up
    // and arrive together — the function is already struggling if we are here.
    const delay = baseDelayMs * 2 ** (attempt - 1);
    await sleep(delay + Math.floor(Math.random() * 200));
  }

  throw new SheetUnavailableError(
    "The patient sheet is temporarily unavailable.",
    { status: lastStatus, attempts: retries + 1, cause: lastCause }
  );
}

/**
 * Write patient fields back to the legacy sheet (updatePatientInSheet).
 *
 * Deliberately NOT retried, unlike the read above. A read is safe to repeat; a
 * write is not obviously so, and a retry that succeeds after a timeout the
 * caller already gave up on is worse than a visible failure. If this throws, the
 * caller should surface it rather than assume the save landed.
 *
 * @param {object}   payload             body for updatePatientInSheet; must carry `token`
 * @param {object}   [opts]
 * @param {function} [opts.fetchImpl]    injected for tests
 * @param {function} [opts.authHeaders]  injected for tests
 * @returns {Promise<object>} the parsed response body, or {} if it had none
 * @throws  {SheetUnavailableError} the write did not succeed
 * @throws  {NotSignedInError}      nobody is signed in to authenticate the call
 */
export async function updateSheetPatient(
  payload,
  { fetchImpl, authHeaders = defaultAuthHeaders } = {}
) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };

  let res;
  try {
    res = await doFetch(`${FUNCTIONS_BASE}/updatePatientInSheet`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw new SheetUnavailableError("Could not save to the patient sheet.", {
      status: null,
      attempts: 1,
      cause: err,
    });
  }

  if (!res.ok) {
    throw new SheetUnavailableError("Could not save to the patient sheet.", {
      status: typeof res.status === "number" ? res.status : null,
      attempts: 1,
      cause: new Error(`HTTP ${res.status}`),
    });
  }

  // The function replies with plain text on some paths, so a missing or
  // unparseable body is not a failure — the status already said it worked.
  return res.json ? res.json().catch(() => ({})) : {};
}

/**
 * Write only study enrollments (and optionally status) for legacy Sheet
 * patients, via updateSheetEnrollment. The server merges `enrolledStudies`
 * into each row's current CLINICAL_DATA and changes nothing else, so this is
 * safe to call from pages that don't hold the whole patient record.
 *
 * Not retried, for the same reason as updateSheetPatient.
 *
 * @param {Array<{token, enrolledStudies, status?, expectStatus?}>} updates
 * @returns {Promise<Array<{token, ok, statusChanged?, statusSkipped?, error?}>>}
 * @throws  {SheetUnavailableError} the call did not succeed
 */
export async function updateSheetEnrollment(
  updates,
  { fetchImpl, authHeaders = defaultAuthHeaders } = {}
) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };

  let res;
  try {
    res = await doFetch(`${FUNCTIONS_BASE}/updateSheetEnrollment`, {
      method: "POST",
      headers,
      body: JSON.stringify({ updates }),
    });
  } catch (err) {
    throw new SheetUnavailableError("Could not save to the patient sheet.", {
      status: null,
      attempts: 1,
      cause: err,
    });
  }
  if (!res.ok) {
    throw new SheetUnavailableError("Could not save to the patient sheet.", {
      status: typeof res.status === "number" ? res.status : null,
      attempts: 1,
      cause: new Error(`HTTP ${res.status}`),
    });
  }
  const body = await res.json().catch(() => ({}));
  return Array.isArray(body.results) ? body.results : [];
}

/**
 * Thrown when a narrow Sheet write was answered with a definite "no" (changed
 * meanwhile, not allowed, bad input). `changed` is the 409 "someone changed it
 * since you looked" case; `message` is the server's reason, safe to show.
 */
export class SheetWriteRefusedError extends Error {
  constructor(message, { status = null, changed = false } = {}) {
    super(message);
    this.name = "SheetWriteRefusedError";
    this.status = status;
    this.changed = changed;
  }
}

// One POST to a narrow, one-column Sheet write (sheetNarrowWrites.js on the
// server). Not retried, for the same reason as updateSheetPatient.
async function postNarrowSheetWrite(fnName, body, { fetchImpl, authHeaders = defaultAuthHeaders } = {}) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };
  let res;
  try {
    res = await doFetch(`${FUNCTIONS_BASE}/${fnName}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new SheetUnavailableError("Could not save to the patient sheet.", { status: null, attempts: 1, cause: err });
  }
  const reply = res.json ? await res.json().catch(() => ({})) : {};
  if (res.ok) return reply || {};
  const status = typeof res.status === "number" ? res.status : null;
  if (status !== null && status >= 400 && status < 500) {
    const changed = status === 409 && reply.error === "changed";
    throw new SheetWriteRefusedError(
      changed
        ? "Someone changed this patient in the Sheet since you opened it. Reload and try again."
        : reply.error || "The patient sheet refused this change.",
      { status, changed }
    );
  }
  throw new SheetUnavailableError("Could not save to the patient sheet.", {
    status,
    attempts: 1,
    cause: new Error(`HTTP ${res.status}`),
  });
}

/**
 * An admin's MRN edit for a Sheet patient (updateSheetPatientMrn). Writes only
 * the MRN column, and only if it still holds `from` (what the page loaded).
 * The server checks the caller is a site admin.
 */
export const updateSheetPatientMrn = ({ token, from, to }, opts) =>
  postNarrowSheetWrite("updateSheetPatientMrn", { token, from: from || "", to }, opts);

/**
 * Scan for Duplicates' move for a Sheet patient (markSheetPatientDuplicate).
 * Sets only STATUS = "Potential Duplicate", and only if the row's status is
 * still `expectStatus`. Nothing is copied or deleted. The server also writes the
 * patient-history event; resolves `{ audited }` saying whether it could.
 */
export const markSheetPatientDuplicate = ({ token, expectStatus }, opts) =>
  postNarrowSheetWrite("markSheetPatientDuplicate", { token, expectStatus: expectStatus || "" }, opts);
